import type { AppNotification } from '@digital-twins/shared-types'
import type { ParseKeys, TFunction } from 'i18next'

import { intlLocaleForUi } from '@/i18n'
import { audienceDisplayName } from '@/lib/audience-display-name'

const KNOWN_ANNOUNCEMENT_TITLE_KEYS: Record<string, ParseKeys<'app'>> = {
  'Introducing smarter audience insights': 'notifications.mockInsightsTitle',
  'New: Focus groups now support up to 8 participants': 'notifications.mockFocusGroups',
}

const KNOWN_ANNOUNCEMENT_BODY_KEYS: Record<string, ParseKeys<'app'>> = {
  'Explore new ways to compare audiences, run faster studies, and share results with your team.':
    'notifications.mockInsightsBody',
}

function asString(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return null
}

function metaString(
  metadata: Record<string, unknown> | undefined,
  keys: Array<string>,
): string | null {
  if (!metadata) {
    return null
  }
  for (const key of keys) {
    const value = asString(metadata[key])
    if (value) {
      return value
    }
  }
  return null
}

function daysUntil(value: unknown, nowMs: number): number | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }
  const parsed = Date.parse(value)
  if (!Number.isFinite(parsed)) {
    return null
  }
  return Math.ceil((parsed - nowMs) / 86_400_000)
}

export interface NotificationDisplayCopy {
  title: string
  body: string | null
}

/**
 * Remap stored English templates to UI chrome. Entity names stay as stored content.
 * CS announcements without a known title/body key keep the stored copy.
 */
export function formatNotificationDisplay(
  notification: AppNotification,
  t: TFunction<'app'>,
  nowMs: number = Date.now(),
): NotificationDisplayCopy {
  const metadata = notification.metadata
  const fallback = notification.message

  switch (notification.type) {
    // A Topic paused because its payer ran out of credits (one per pause; the stored `href` opens the Topic).
    case 'social_topic_paused': {
      const topic = metaString(metadata, ['topicName', 'universeName'])
      if (!topic) {
        return { title: fallback, body: null }
      }
      return { title: t('notifications.socialTopicPaused', { topic }), body: null }
    }
    case 'study_completed':
      return {
        title: t('notifications.studyCompleted', {
          name: metaString(metadata, ['studyName']) ?? t('notifications.untitledStudy'),
        }),
        body: null,
      }
    case 'study_failed':
      return {
        title: t('notifications.studyFailed', {
          name: metaString(metadata, ['studyName']) ?? t('notifications.untitledStudy'),
        }),
        body: null,
      }
    case 'audience_ready':
      return {
        title: t('notifications.audienceReady', {
          name: audienceDisplayName(metaString(metadata, ['audienceName'])) || t('notifications.untitledAudience'),
        }),
        body: null,
      }
    case 'audience_failed':
      return {
        title: t('notifications.audienceFailed', {
          name: audienceDisplayName(metaString(metadata, ['audienceName'])) || t('notifications.untitledAudience'),
        }),
        body: null,
      }
    case 'chat_shared': {
      const sharer = metaString(metadata, ['sharerName', 'memberName', 'inviterName'])
      const chatName = metaString(metadata, ['chatName']) ?? t('notifications.untitledChat')
      if (sharer) {
        return { title: t('notifications.chatShared', { name: sharer }), body: null }
      }
      return { title: t('notifications.chatSharedOwned', { name: chatName }), body: null }
    }
    case 'chat_viewed': {
      const count = asCount(metadata?.viewerCount) ?? 1
      return {
        title: t('notifications.chatViewed', {
          count,
          name: metaString(metadata, ['chatName']) ?? t('notifications.untitledChat'),
        }),
        body: null,
      }
    }
    case 'referral_credit': {
      const amount = asString(metadata?.creditAmount)
      if (!amount) {
        return { title: fallback, body: null }
      }
      return { title: t('notifications.referralCredit', { amount }), body: null }
    }
    case 'credit_topup': {
      const amount = asString(metadata?.creditAmount)
      if (!amount) {
        return { title: fallback, body: null }
      }
      return { title: t('notifications.creditTopup', { amount }), body: null }
    }
    case 'low_credit': {
      const amount = asString(metadata?.creditBalance)
      if (!amount) {
        return { title: fallback, body: null }
      }
      return { title: t('notifications.lowCredit', { amount }), body: null }
    }
    case 'team_member_joined': {
      const name = metaString(metadata, ['memberName'])
      if (!name) {
        return { title: fallback, body: null }
      }
      const org = metaString(metadata, ['organizationName'])
      if (org) {
        return { title: t('notifications.teamJoined', { name, org }), body: null }
      }
      return { title: t('notifications.teamJoinedWorkspace', { name }), body: null }
    }
    case 'trial_expiring': {
      const remaining =
        asCount(metadata?.daysRemaining) ?? daysUntil(metadata?.expiresAt, nowMs)
      if (remaining === null) {
        return { title: t('notifications.trialExpiringSoon'), body: null }
      }
      if (remaining <= 1) {
        return { title: t('notifications.trialExpiringSoon'), body: null }
      }
      return { title: t('notifications.trialExpiring', { count: remaining }), body: null }
    }
    case 'audience_shared': {
      const inviter = metaString(metadata, ['inviterName'])
      const name = audienceDisplayName(metaString(metadata, ['audienceName'])) || t('notifications.untitledAudience')
      if (!inviter) {
        return { title: fallback, body: null }
      }
      return { title: t('notifications.audienceShared', { inviter, name }), body: null }
    }
    case 'audience_invite_accepted': {
      const name = metaString(metadata, ['accepterName'])
      const audience =
        audienceDisplayName(metaString(metadata, ['audienceName'])) || t('notifications.untitledAudience')
      if (!name) {
        return { title: fallback, body: null }
      }
      return {
        title: t('notifications.audienceInviteAccepted', { name, audience }),
        body: null,
      }
    }
    case 'slack_account_linked': {
      const workspace = metaString(metadata, ['workspaceName'])
      if (!workspace) {
        return { title: fallback, body: null }
      }
      return {
        title: t('notifications.slackAccountLinked', { workspace }),
        body: t('notifications.slackAccountLinkedBody'),
      }
    }
    case 'cs_announcement':
      return formatAnnouncement(notification, t)
    default:
      return { title: fallback, body: null }
  }
}

function formatAnnouncement(
  notification: AppNotification,
  t: TFunction<'app'>,
): NotificationDisplayCopy {
  const metadata = notification.metadata
  // Stored notifications name their copy by an app key (written server-side).
  const titleKey = asString(metadata?.titleKey) as ParseKeys<'app'> | null
  const titleFromMap = KNOWN_ANNOUNCEMENT_TITLE_KEYS[notification.message]
  const title = titleKey
    ? t(titleKey)
    : titleFromMap
      ? t(titleFromMap)
      : notification.message

  const storedBody = asString(metadata?.body) ?? asString(metadata?.text)
  const bodyKey = asString(metadata?.bodyKey) as ParseKeys<'app'> | null
  const bodyFromMap = storedBody ? KNOWN_ANNOUNCEMENT_BODY_KEYS[storedBody] : undefined
  const body = bodyKey ? t(bodyKey) : bodyFromMap ? t(bodyFromMap) : storedBody

  return { title, body }
}

export function formatNotificationRelativeTime(
  epochMs: number,
  t: TFunction<'app'>,
  lng: string | undefined,
  nowMs: number = Date.now(),
): string {
  const diffMs = nowMs - epochMs
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMins < 1) {
    return t('notifications.justNow')
  }
  if (diffMins < 60) {
    return t('notifications.minutesAgo', { count: diffMins })
  }
  if (diffHours < 24) {
    return t('notifications.hoursAgo', { count: diffHours })
  }
  if (diffDays < 30) {
    return t('notifications.daysAgo', { count: diffDays })
  }
  return new Date(epochMs).toLocaleDateString(intlLocaleForUi(lng))
}
